// src/pages/Admin.js
import React, { useEffect, useMemo, useState } from "react";
import games from "../data/games";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  where
} from "firebase/firestore";
import { onValue, ref as dbRef } from "firebase/database";
import { db, database, storage } from "../firebase";
import {
  PRESENCE_COLLECTION_GUESTS,
  PRESENCE_COLLECTION_USERS,
  PRESENCE_PATH_GUESTS,
  PRESENCE_PATH_USERS,
  USE_FIRESTORE_PRESENCE
} from "../utils/presence";
import Seo from "../components/Seo";

const ADMIN_LOGIN = "admin";
const ADMIN_PASSWORD = "Aa060834812@";
const AUTH_KEY = "up-admin-auth";
const FEATURED_KEY = "up-featured-config";
const BANNER_TYPES = {
  DEFAULT: "default",
  GAME: "game"
};

const createBannerId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const normalizeBanner = (value) => {
  if (typeof value === "string") {
    return {
      id: createBannerId(),
      type: BANNER_TYPES.GAME,
      gameId: value,
      image: "",
      text: "",
      url: ""
    };
  }

  if (!value || typeof value !== "object") return null;

  const type =
    value.type === BANNER_TYPES.DEFAULT ? BANNER_TYPES.DEFAULT : BANNER_TYPES.GAME;

  return {
    id: typeof value.id === "string" && value.id ? value.id : createBannerId(),
    type,
    gameId: typeof value.gameId === "string" ? value.gameId : "",
    image: typeof value.image === "string" ? value.image : "",
    text: typeof value.text === "string" ? value.text : "",
    url: typeof value.url === "string" ? value.url : ""
  };
};

const normalizeFeaturedConfig = (value) => {
  if (!value || typeof value !== "object") {
    return { banners: [], recommended: [] };
  }

  const rawBanners = Array.isArray(value.banners) ? value.banners : [];
  const rawRecommended = Array.isArray(value.recommended) ? value.recommended : [];

  return {
    banners: rawBanners.map(normalizeBanner).filter(Boolean),
    recommended: rawRecommended.filter((id) => typeof id === "string")
  };
};

const createEmptyBannerDraft = () => ({
  type: BANNER_TYPES.DEFAULT,
  gameId: "",
  image: "",
  text: "",
  url: ""
});

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

const formatAdminDate = (value) => {
  if (!value) return "Just now";
  const date =
    typeof value?.toDate === "function"
      ? value.toDate()
      : value instanceof Date
        ? value
        : typeof value?.seconds === "number"
          ? new Date(value.seconds * 1000)
          : null;
  if (!date) return "Just now";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const formatDurationShort = (seconds) => {
  const total = Number(seconds) || 0;
  if (total <= 0) return "0m";
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours && minutes) return `${hours}h ${minutes}m`;
  if (hours) return `${hours}h`;
  return `${minutes}m`;
};

const sortedCounterEntries = (counterRaw) => {
  if (!counterRaw || typeof counterRaw !== "object") return [];
  return Object.entries(counterRaw)
    .map(([key, value]) => [key, Number(value) || 0])
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);
};

const Admin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isAuthed, setIsAuthed] = useState(false);
  const [error, setError] = useState("");
  const [config, setConfig] = useState({ banners: [], recommended: [] });
  const [bannerDraft, setBannerDraft] = useState(createEmptyBannerDraft);
  const [bannerError, setBannerError] = useState("");
  const [bannerHint, setBannerHint] = useState("");
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [editingBannerId, setEditingBannerId] = useState("");
  const [newsTitle, setNewsTitle] = useState("");
  const [newsContent, setNewsContent] = useState("");
  const [newsImage, setNewsImage] = useState("");
  const [editingNewsId, setEditingNewsId] = useState("");
  const [newsItems, setNewsItems] = useState([]);
  const [newsError, setNewsError] = useState("");
  const [newsInfo, setNewsInfo] = useState("");
  const [savingNews, setSavingNews] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState("featured");
  const [usersOnline, setUsersOnline] = useState(0);
  const [guestsOnline, setGuestsOnline] = useState(0);
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogSlug, setBlogSlug] = useState("");
  const [blogTitle, setBlogTitle] = useState("");
  const [blogContent, setBlogContent] = useState("");
  const [blogImage, setBlogImage] = useState("");
  const [editingBlogId, setEditingBlogId] = useState("");
  const [blogError, setBlogError] = useState("");
  const [blogInfo, setBlogInfo] = useState("");
  const [savingBlog, setSavingBlog] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const token = window.localStorage.getItem(AUTH_KEY);
    if (token === "true") {
      setIsAuthed(true);
    }

    const loadConfig = async () => {
      try {
        const snap = await getDoc(doc(db, "siteConfig", "featured"));
        if (snap.exists()) {
          setConfig(normalizeFeaturedConfig(snap.data()));
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to load featured config from Firestore:", err);
        // fallback: try localStorage for backwards compat
        try {
          const raw = window.localStorage.getItem(FEATURED_KEY);
          if (raw) {
            setConfig(normalizeFeaturedConfig(JSON.parse(raw)));
          }
        } catch {
          // ignore
        }
      }
    };
    loadConfig();
  }, []);
  useEffect(() => {
    if (!isAuthed) return undefined;

    const unsubNews = onSnapshot(
      query(collection(db, "newsPosts"), orderBy("createdAt", "desc")),
      (snapshot) => {
        const next = snapshot.docs.map((item) => ({ id: item.id, ...(item.data() || {}) }));
        setNewsItems(next);
      },
      () => {
        setNewsItems([]);
      }
    );
    return () => unsubNews();
  }, [isAuthed]);

  useEffect(() => {
    if (!isAuthed) return undefined;
    const unsubBlog = onSnapshot(
      query(collection(db, "blogPosts"), orderBy("createdAt", "desc")),
      (snapshot) => {
        const next = snapshot.docs.map((item) => ({ id: item.id, ...(item.data() || {}) }));
        setBlogPosts(next);
      },
      () => setBlogPosts([])
    );
    return () => unsubBlog();
  }, [isAuthed]);

  useEffect(() => {
    if (!isAuthed) return undefined;
    setUsersLoading(true);
    setUsersError("");

    const usersRef = collection(db, "users");
    const qUsers = query(usersRef, orderBy("createdAt", "desc"));

    const unsubUsers = onSnapshot(
      qUsers,
      (snapshot) => {
        const list = snapshot.docs.map((docSnap) => {
          const data = docSnap.data() || {};
          return {
            id: docSnap.id,
            ...data
          };
        });
        setUsers(list);
        setUsersLoading(false);
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error("Failed to load users for admin:", err);
        setUsers([]);
        setUsersError(err?.message || "Failed to load users.");
        setUsersLoading(false);
      }
    );

    return () => unsubUsers();
  }, [isAuthed]);

  useEffect(() => {
    if (!isAuthed) return;

    if (database) {
      const usersRef = dbRef(database, PRESENCE_PATH_USERS);
      const guestsRef = dbRef(database, PRESENCE_PATH_GUESTS);
      const unsubUsers = onValue(usersRef, (snap) => {
        const val = snap.val();
        setUsersOnline(typeof val === "object" && val !== null ? Object.keys(val).length : 0);
      });
      const unsubGuests = onValue(guestsRef, (snap) => {
        const val = snap.val();
        setGuestsOnline(typeof val === "object" && val !== null ? Object.keys(val).length : 0);
      });
      return () => {
        unsubUsers();
        unsubGuests();
      };
    }

  }, [isAuthed]);

  const handleRefreshOnlineStats = () => {
    if (!USE_FIRESTORE_PRESENCE) return;
    const cutoff = Timestamp.fromMillis(Date.now() - 2 * 60 * 1000);
    const qUsers = query(
      collection(db, PRESENCE_COLLECTION_USERS),
      where("lastSeen", ">=", cutoff)
    );
    const qGuests = query(
      collection(db, PRESENCE_COLLECTION_GUESTS),
      where("lastSeen", ">=", cutoff)
    );
    Promise.all([getDocs(qUsers), getDocs(qGuests)])
      .then(([userSnap, guestSnap]) => {
        setUsersOnline(userSnap.size);
        setGuestsOnline(guestSnap.size);
      })
      .catch(() => {});
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (username === ADMIN_LOGIN && password === ADMIN_PASSWORD) {
      setIsAuthed(true);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(AUTH_KEY, "true");
      }
      setError("");
    } else {
      setError("Invalid credentials");
    }
  };

  const toggleSelection = (kind, id) => {
    setConfig((prev) => {
      const current = new Set(prev[kind] || []);
      if (current.has(id)) {
        current.delete(id);
      } else {
        current.add(id);
      }
      return { ...prev, [kind]: Array.from(current) };
    });
  };

  const gameById = useMemo(() => {
    const map = new Map();
    games.forEach((game) => map.set(game.id, game));
    return map;
  }, []);

  const selectedUserStats = useMemo(() => {
    if (!selectedUser) return null;
    const stats = selectedUser;

    const gamesPlayed = Number(stats.totalPlays) || 0;
    const streakCount = Number(stats.streakCount) || 0;
    const totalPlaytimeSeconds = Number(stats.totalPlaytimeSeconds) || 0;
    const averageSessionSeconds =
      gamesPlayed > 0 ? Math.round(totalPlaytimeSeconds / gamesPlayed) : 0;

    const recentRaw = Array.isArray(stats.recentGames) ? stats.recentGames : [];
    const recentSessions = recentRaw.slice(0, 5).map((entry) => {
      const full = gameById.get(entry.id);
      return {
        ...entry,
        title: entry.title || full?.title || "Game",
        slug: entry.slug || full?.slug || "",
        thumbnail: entry.thumbnail || full?.thumbnail || "",
        category: full?.category || "",
        lastPlayedAtDate:
          entry.lastPlayedAt && typeof entry.lastPlayedAt.seconds === "number"
            ? new Date(entry.lastPlayedAt.seconds * 1000)
            : null
      };
    });

    const favoritesRaw = Array.isArray(stats.favoriteGames) ? stats.favoriteGames : [];
    const favoriteGames = favoritesRaw.slice(0, 5).map((fav) => {
      const full = gameById.get(fav.id);
      return {
        ...fav,
        title: fav.title || full?.title || "Game",
        slug: fav.slug || full?.slug || "",
        thumbnail: fav.thumbnail || full?.thumbnail || ""
      };
    });

    const genreEntries = sortedCounterEntries(stats.genrePlayCounts).slice(0, 5);
    const categoryEntries = sortedCounterEntries(stats.categoryPlayCounts).slice(0, 4);

    const topPlayedGame = (() => {
      const byGame = stats.playtimeByGameId;
      if (!byGame || typeof byGame !== "object") return null;
      const [topId, seconds] =
        Object.entries(byGame).sort(
          (a, b) => (Number(b[1]) || 0) - (Number(a[1]) || 0)
        )[0] || [];
      if (!topId) return null;
      const full = gameById.get(topId);
      return {
        id: topId,
        title: full?.title || topId,
        slug: full?.slug || "",
        seconds: Number(seconds) || 0
      };
    })();

    const registeredAt = stats.createdAt ? formatAdminDate(stats.createdAt) : "Unknown";
    const username = (stats.username || stats.displayName || "").trim() || "Anonymous";

    return {
      gamesPlayed,
      streakCount,
      totalPlaytimeSeconds,
      averageSessionSeconds,
      recentSessions,
      favoriteGames,
      genreEntries,
      categoryEntries,
      topPlayedGame,
      registeredAt,
      username
    };
  }, [selectedUser, gameById]);

  const validateBanners = (banners) => {
    for (const banner of banners) {
      if (banner.type === BANNER_TYPES.DEFAULT) {
        if (!banner.image.trim()) {
          return "Default banner image is required.";
        }
        if (!banner.text.trim()) {
          return "Default banner text is required.";
        }
        if (!banner.url.trim()) {
          return "Default banner URL is required.";
        }
      } else if (!banner.gameId || !gameById.has(banner.gameId)) {
        return "Each game banner must be linked to an existing game.";
      }
    }
    return "";
  };

  const handleSave = async () => {
    const validationError = validateBanners(config.banners || []);
    if (validationError) {
      setBannerError(validationError);
      return;
    }

    try {
      await setDoc(doc(db, "siteConfig", "featured"), {
        banners: config.banners || [],
        recommended: config.recommended || []
      });
      // also keep localStorage as local cache
      if (typeof window !== "undefined") {
        window.localStorage.setItem(FEATURED_KEY, JSON.stringify(config));
      }
      setBannerError("");
      // eslint-disable-next-line no-alert
      alert("Featured configuration saved to cloud.");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to save featured config to Firestore:", err);
      setBannerError(
        "Failed to save to cloud. Check Firestore rules and Firebase env config. Details: " +
          (err.message || "Unknown error")
      );
    }
  };

  const clearSelection = async () => {
    setConfig({ banners: [], recommended: [] });
    setBannerDraft(createEmptyBannerDraft());
    setEditingBannerId("");
    setBannerError("");
    setBannerHint("");
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(FEATURED_KEY);
    }
    try {
      await setDoc(doc(db, "siteConfig", "featured"), {
        banners: [],
        recommended: []
      });
    } catch {
      // ignore clear errors
    }
  };

  const sortedGames = useMemo(
    () =>
      [...games].sort((a, b) =>
        a.title.localeCompare(b.title, "en", { sensitivity: "base" })
      ),
    []
  );

  const uploadBannerImage = async (file) => {
    const safeName = (file.name || "banner").replace(/\s+/g, "-").toLowerCase();
    const storageRef = ref(storage, `banners/${Date.now()}-${safeName}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  const handleBannerImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setBannerError("");
    setBannerHint("");
    setIsUploadingImage(true);
    try {
      const localImageUrl = await fileToDataUrl(file);
      if (localImageUrl) {
        setBannerDraft((prev) => ({ ...prev, image: localImageUrl }));
        setBannerHint("Image selected. Finalizing upload...");
      }

      try {
        const remoteImageUrl = await uploadBannerImage(file);
        setBannerDraft((prev) => ({ ...prev, image: remoteImageUrl }));
        setBannerHint("Image uploaded and ready.");
      } catch {
        // Keep local data URL fallback if public URL cannot be generated.
        if (localImageUrl) {
          setBannerHint("Image added using local fallback (cloud URL unavailable).");
        } else {
          setBannerError("Image upload failed. Please try again.");
        }
      }
    } catch {
      setBannerError("Image processing failed. Please try another image.");
    } finally {
      setIsUploadingImage(false);
      e.target.value = "";
    }
  };

  const handleSubmitBannerDraft = () => {
    const next = {
      id: editingBannerId || createBannerId(),
      type: bannerDraft.type,
      gameId: bannerDraft.gameId.trim(),
      image: bannerDraft.image.trim(),
      text: bannerDraft.text.trim(),
      url: bannerDraft.url.trim()
    };

    if (next.type === BANNER_TYPES.DEFAULT) {
      if (!next.image) {
        setBannerError("Upload an image for a default banner.");
        return;
      }
      if (!next.text) {
        setBannerError("Text is required for a default banner.");
        return;
      }
      if (!next.url) {
        setBannerError("URL is required for a default banner.");
        return;
      }
    } else if (!next.gameId || !gameById.has(next.gameId)) {
      setBannerError("Select a game for a game banner.");
      return;
    }

    setConfig((prev) => {
      if (!editingBannerId) {
        return { ...prev, banners: [...(prev.banners || []), next] };
      }

      return {
        ...prev,
        banners: (prev.banners || []).map((b) =>
          b.id === editingBannerId ? next : b
        )
      };
    });
    setBannerDraft(createEmptyBannerDraft());
    setEditingBannerId("");
    setBannerError("");
    setBannerHint("");
  };

  const startEditBanner = (banner) => {
    setEditingBannerId(banner.id);
    setBannerDraft({
      type: banner.type,
      gameId: banner.gameId || "",
      image: banner.image || "",
      text: banner.text || "",
      url: banner.url || ""
    });
    setBannerError("");
    setBannerHint(banner.image ? "Current image loaded." : "");
  };

  const cancelEditBanner = () => {
    setEditingBannerId("");
    setBannerDraft(createEmptyBannerDraft());
    setBannerError("");
    setBannerHint("");
  };

  const removeBanner = (id) => {
    setConfig((prev) => ({
      ...prev,
      banners: (prev.banners || []).filter((b) => b.id !== id)
    }));
    if (editingBannerId === id) {
      cancelEditBanner();
    }
  };
  const handleCreateNewsPost = async () => {
    const title = newsTitle.trim();
    const content = newsContent.trim();
    const image = newsImage.trim();
    if (!title) {
      setNewsError("News title is required.");
      return;
    }
    if (!content) {
      setNewsError("News content is required.");
      return;
    }

    setSavingNews(true);
    setNewsError("");
    setNewsInfo("");
    try {
      if (editingNewsId) {
        await setDoc(
          doc(db, "newsPosts", editingNewsId),
          {
            title,
            content,
            image,
            published: true,
            updatedAt: serverTimestamp()
          },
          { merge: true }
        );
      } else {
        await addDoc(collection(db, "newsPosts"), {
          title,
          content,
          image,
          published: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      setNewsTitle("");
      setNewsContent("");
      setNewsImage("");
      setEditingNewsId("");
      setNewsInfo(editingNewsId ? "News post updated." : "News post published.");
    } catch (err) {
      setNewsError(err?.message || `Failed to ${editingNewsId ? "update" : "publish"} news post.`);
    } finally {
      setSavingNews(false);
    }
  };
  const startEditNewsPost = (item) => {
    if (!item) return;
    setEditingNewsId(item.id);
    setNewsTitle(item.title || "");
    setNewsContent(item.content || "");
    setNewsImage(item.image || "");
    setNewsError("");
    setNewsInfo("Editing selected news post.");
  };
  const cancelEditNewsPost = () => {
    setEditingNewsId("");
    setNewsTitle("");
    setNewsContent("");
    setNewsImage("");
    setNewsError("");
    setNewsInfo("Edit canceled.");
  };
  const handleDeleteNewsPost = async (newsId) => {
    if (!newsId) return;
    setSavingNews(true);
    setNewsError("");
    setNewsInfo("");
    try {
      await deleteDoc(doc(db, "newsPosts", newsId));
      if (editingNewsId === newsId) {
        cancelEditNewsPost();
      }
      setNewsInfo("News post deleted.");
    } catch (err) {
      setNewsError(err?.message || "Failed to delete news post.");
    } finally {
      setSavingNews(false);
    }
  };

  const slugFromTitle = (title) =>
    (title || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");

  const handleCreateBlogPost = async () => {
    const slug = (blogSlug || slugFromTitle(blogTitle)).trim();
    const title = blogTitle.trim();
    const content = blogContent.trim();
    const image = blogImage.trim();

    if (!slug) {
      setBlogError("Slug is required (used in URL, e.g. my-first-post).");
      return;
    }
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
      setBlogError("Slug must be URL-safe: lowercase letters, numbers, and hyphens only.");
      return;
    }
    if (!title) {
      setBlogError("Title is required.");
      return;
    }
    if (!content) {
      setBlogError("Content is required.");
      return;
    }

    const slugExists = blogPosts.some(
      (p) => (p.slug || "").toLowerCase() === slug.toLowerCase() && p.id !== editingBlogId
    );
    if (slugExists) {
      setBlogError("A post with this slug already exists.");
      return;
    }

    setSavingBlog(true);
    setBlogError("");
    setBlogInfo("");
    try {
      const payload = {
        slug,
        title,
        content,
        image: image || null,
        published: true,
        updatedAt: serverTimestamp()
      };
      if (editingBlogId) {
        await setDoc(doc(db, "blogPosts", editingBlogId), payload, { merge: true });
        setBlogInfo("Blog post updated. URL: /blog/" + slug);
      } else {
        await addDoc(collection(db, "blogPosts"), {
          ...payload,
          createdAt: serverTimestamp()
        });
        setBlogInfo("Blog post published. URL: /blog/" + slug);
      }
      setBlogSlug("");
      setBlogTitle("");
      setBlogContent("");
      setBlogImage("");
      setEditingBlogId("");
    } catch (err) {
      setBlogError(err?.message || "Failed to save blog post.");
    } finally {
      setSavingBlog(false);
    }
  };

  const startEditBlogPost = (item) => {
    if (!item) return;
    setEditingBlogId(item.id);
    setBlogSlug(item.slug || "");
    setBlogTitle(item.title || "");
    setBlogContent(item.content || "");
    setBlogImage(item.image || "");
    setBlogError("");
    setBlogInfo("Editing: " + (item.slug || item.id));
  };

  const cancelEditBlogPost = () => {
    setEditingBlogId("");
    setBlogSlug("");
    setBlogTitle("");
    setBlogContent("");
    setBlogImage("");
    setBlogError("");
    setBlogInfo("");
  };

  const handleDeleteBlogPost = async (postId) => {
    if (!postId) return;
    setSavingBlog(true);
    setBlogError("");
    setBlogInfo("");
    try {
      await deleteDoc(doc(db, "blogPosts", postId));
      if (editingBlogId === postId) cancelEditBlogPost();
      setBlogInfo("Blog post deleted.");
    } catch (err) {
      setBlogError(err?.message || "Failed to delete blog post.");
    } finally {
      setSavingBlog(false);
    }
  };

  const handleBlogImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBlogError("");
    setBlogInfo("");
    setSavingBlog(true);
    try {
      const localUrl = await fileToDataUrl(file);
      if (localUrl) setBlogImage(localUrl);
      try {
        const remoteUrl = await uploadBannerImage(file);
        setBlogImage(remoteUrl);
        setBlogInfo("Image uploaded.");
      } catch {
        if (localUrl) setBlogInfo("Using local image fallback.");
        else setBlogError("Image upload failed.");
      }
    } catch {
      setBlogError("Could not process image.");
    } finally {
      setSavingBlog(false);
      e.target.value = "";
    }
  };
  const handleNewsImagePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setNewsError("");
    setNewsInfo("");
    setSavingNews(true);
    try {
      const localImageUrl = await fileToDataUrl(file);
      if (localImageUrl) {
        setNewsImage(localImageUrl);
      }
      try {
        const remoteImageUrl = await uploadBannerImage(file);
        setNewsImage(remoteImageUrl);
        setNewsInfo("News image uploaded and ready.");
      } catch {
        if (localImageUrl) {
          setNewsInfo("Using local image fallback (cloud URL unavailable).");
        } else {
          setNewsError("Could not upload news image.");
        }
      }
    } catch {
      setNewsError("Could not process selected image.");
    } finally {
      setSavingNews(false);
      e.target.value = "";
    }
  };

  if (!isAuthed) {
    return (
      <main className="up-page container">
        <Seo
          title="Admin Login"
          description="Admin panel access for OctoGames featured content management."
          path="/admin"
          noindex
        />
        <div className="admin-panel">
          <h1 className="admin-title">Admin login</h1>
          <form className="admin-form" onSubmit={handleLogin}>
            <label className="admin-label">
              Login
              <input
                className="admin-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </label>
            <label className="admin-label">
              Password
              <input
                type="password"
                className="admin-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            {error && <div className="admin-error">{error}</div>}
            <button type="submit" className="admin-btn-primary">
              Sign in
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className="up-page container">
      <Seo
        title="Admin Panel"
        description="Manage featured banners, recommended games, platform news, and user statistics."
        path="/admin"
        noindex
      />
      <div className="admin-panel">
        <h1 className="admin-title">Admin panel</h1>
        <p className="admin-subtitle">
          Manage featured banners, news posts, and review player statistics.
        </p>

        {(database || USE_FIRESTORE_PRESENCE) ? (
          <div className="admin-online-stats" aria-live="polite">
            <span className="admin-online-item">
              <strong>Users online:</strong> {usersOnline}
            </span>
            <span className="admin-online-item">
              <strong>Guests online:</strong> {guestsOnline}
            </span>
            {USE_FIRESTORE_PRESENCE && (
              <button
                type="button"
                className="admin-online-refresh"
                onClick={handleRefreshOnlineStats}
              >
                Refresh
              </button>
            )}
          </div>
        ) : (
          <p className="admin-online-hint">
            Online counts require Firebase Realtime Database. Set <code>REACT_APP_FIREBASE_DATABASE_URL</code> in your env.
          </p>
        )}

        <div className="admin-tabs">
          <button
            type="button"
            className={
              "admin-tab-btn" + (activeTab === "featured" ? " admin-tab-btn-active" : "")
            }
            onClick={() => setActiveTab("featured")}
          >
            Featured &amp; news
          </button>
          <button
            type="button"
            className={
              "admin-tab-btn" + (activeTab === "blog" ? " admin-tab-btn-active" : "")
            }
            onClick={() => setActiveTab("blog")}
          >
            Blog
          </button>
          <button
            type="button"
            className={
              "admin-tab-btn" + (activeTab === "users" ? " admin-tab-btn-active" : "")
            }
            onClick={() => setActiveTab("users")}
          >
            User stats
          </button>
        </div>

        {activeTab === "featured" && (
          <>
        <div className="admin-legend">
          <span className="admin-pill admin-pill-banner">Banner</span>
          <span className="admin-pill admin-pill-recommended">Recommended</span>
        </div>

        <section className="admin-banner-builder">
          <h2 className="admin-section-title">Create banner</h2>

          <div className="admin-banner-grid">
            <label className="admin-label">
              Banner type
              <select
                className="admin-input"
                value={bannerDraft.type}
                onChange={(e) =>
                  setBannerDraft((prev) => ({
                    ...prev,
                    type: e.target.value === BANNER_TYPES.GAME
                      ? BANNER_TYPES.GAME
                      : BANNER_TYPES.DEFAULT,
                    gameId:
                      e.target.value === BANNER_TYPES.GAME ? prev.gameId : "",
                    url:
                      e.target.value === BANNER_TYPES.DEFAULT ? prev.url : ""
                  }))
                }
              >
                <option value={BANNER_TYPES.DEFAULT}>Default</option>
                <option value={BANNER_TYPES.GAME}>Game</option>
              </select>
            </label>

            {bannerDraft.type === BANNER_TYPES.GAME ? (
              <label className="admin-label">
                Game URL source
                <select
                  className="admin-input"
                  value={bannerDraft.gameId}
                  onChange={(e) =>
                    setBannerDraft((prev) => ({ ...prev, gameId: e.target.value }))
                  }
                >
                  <option value="">Select game</option>
                  {sortedGames.map((game) => (
                    <option key={game.id} value={game.id}>
                      {game.title}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <label className="admin-label">
                URL
                <input
                  className="admin-input"
                  placeholder="https://example.com or /game/slug"
                  value={bannerDraft.url}
                  onChange={(e) =>
                    setBannerDraft((prev) => ({ ...prev, url: e.target.value }))
                  }
                />
              </label>
            )}

            <label className="admin-label">
              Text
              <input
                className="admin-input"
                placeholder={
                  bannerDraft.type === BANNER_TYPES.DEFAULT
                    ? "Required for default banner"
                    : "Optional for game banner"
                }
                value={bannerDraft.text}
                onChange={(e) =>
                  setBannerDraft((prev) => ({ ...prev, text: e.target.value }))
                }
              />
            </label>

            <label className="admin-label">
              Upload image {bannerDraft.type === BANNER_TYPES.DEFAULT ? "(required)" : "(optional)"}
              <input
                type="file"
                accept="image/*"
                className="admin-input"
                onChange={handleBannerImagePick}
                disabled={isUploadingImage}
              />
            </label>
          </div>

          {isUploadingImage && <div className="admin-hint">Uploading image...</div>}

          {bannerHint && <div className="admin-hint">{bannerHint}</div>}

          <button
            type="button"
            className="admin-btn-primary"
            onClick={handleSubmitBannerDraft}
            disabled={isUploadingImage}
          >
            {editingBannerId ? "Update banner" : "Add banner"}
          </button>
          {editingBannerId && (
            <button
              type="button"
              className="admin-btn-secondary"
              onClick={cancelEditBanner}
              disabled={isUploadingImage}
            >
              Cancel edit
            </button>
          )}

          {bannerError && <div className="admin-error">{bannerError}</div>}

          <div className="admin-banner-list">
            {(config.banners || []).map((banner) => {
              const linkedGame = banner.gameId ? gameById.get(banner.gameId) : null;
              return (
                <div key={banner.id} className="admin-banner-item">
                  <div className="admin-banner-copy">
                    <strong>{banner.type === BANNER_TYPES.DEFAULT ? "Default" : "Game"}</strong>
                    {banner.type === BANNER_TYPES.GAME && linkedGame && (
                      <span> - {linkedGame.title}</span>
                    )}
                    {banner.text && <span> - "{banner.text}"</span>}
                    {banner.image && <span> - image set</span>}
                  </div>
                  <div className="admin-banner-actions">
                    <button
                      type="button"
                      className="admin-btn-secondary"
                      onClick={() => startEditBanner(banner)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="admin-btn-secondary"
                      onClick={() => removeBanner(banner.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
            {(!config.banners || config.banners.length === 0) && (
              <div className="admin-hint">No banners yet.</div>
            )}
          </div>
        </section>

        <h2 className="admin-section-title">Recommended games</h2>
        <div className="admin-table">
          <div className="admin-table-header">
            <span>Game</span>
            <span>Category</span>
            <span>Recommended</span>
          </div>
          {sortedGames.map((g) => (
            <div key={g.id} className="admin-row">
              <div className="admin-cell-title">{g.title}</div>
              <div className="admin-cell">{g.category}</div>
              <div className="admin-cell">
                <input
                  type="checkbox"
                  checked={config.recommended?.includes(g.id)}
                  onChange={() => toggleSelection("recommended", g.id)}
                />
              </div>
            </div>
          ))}
        </div>

        <section className="admin-news-builder">
          <h2 className="admin-section-title">News Hub posts</h2>
          <p className="admin-subtitle">
            Publish updates for players. Unread counters in header will update automatically.
          </p>
          {editingNewsId && (
            <div className="admin-hint">Editing mode: update the fields and save changes.</div>
          )}
          <div className="admin-news-grid">
            <label className="admin-label">
              Title
              <input
                className="admin-input"
                placeholder="New platform update"
                value={newsTitle}
                onChange={(e) => setNewsTitle(e.target.value)}
              />
            </label>
            <label className="admin-label">
              Content
              <textarea
                className="admin-input admin-textarea"
                placeholder="Write the update details for players..."
                value={newsContent}
                onChange={(e) => setNewsContent(e.target.value)}
              />
            </label>
            <label className="admin-label">
              Image URL (optional)
              <input
                className="admin-input"
                placeholder="https://.../image.jpg"
                value={newsImage}
                onChange={(e) => setNewsImage(e.target.value)}
              />
            </label>
            <label className="admin-label">
              Upload image (optional)
              <input
                type="file"
                accept="image/*"
                className="admin-input"
                onChange={handleNewsImagePick}
                disabled={savingNews}
              />
            </label>
          </div>
          {newsImage && (
            <div className="admin-news-image-preview-wrap">
              <img src={newsImage} alt="News preview" className="admin-news-image-preview" />
            </div>
          )}
          <div className="admin-news-actions">
            <button
              type="button"
              className="admin-btn-primary"
              onClick={handleCreateNewsPost}
              disabled={savingNews}
            >
              {savingNews ? (editingNewsId ? "Saving..." : "Publishing...") : (editingNewsId ? "Save changes" : "Publish news")}
            </button>
            {editingNewsId && (
              <button
                type="button"
                className="admin-btn-secondary"
                onClick={cancelEditNewsPost}
                disabled={savingNews}
              >
                Cancel edit
              </button>
            )}
          </div>
          {newsError && <div className="admin-error">{newsError}</div>}
          {newsInfo && <div className="admin-hint">{newsInfo}</div>}
          <div className="admin-news-list">
            {newsItems.map((item) => (
              <div key={item.id} className="admin-news-item">
                <div className="admin-news-copy">
                  <strong>{item.title || "Untitled news"}</strong>
                  <span>{formatAdminDate(item.createdAt)}</span>
                  <p>{item.content || "No content"}</p>
                </div>
                {item.image && (
                  <img src={item.image} alt={item.title || "News"} className="admin-news-thumb" />
                )}
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => startEditNewsPost(item)}
                  disabled={savingNews}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => handleDeleteNewsPost(item.id)}
                  disabled={savingNews}
                >
                  Delete
                </button>
              </div>
            ))}
            {newsItems.length === 0 && <div className="admin-hint">No news posts yet.</div>}
          </div>
        </section>

        <section className="admin-blog-builder">
          <h2 className="admin-section-title">Blog posts (SEO-friendly URLs)</h2>
          <p className="admin-subtitle">
            Each post gets its own URL (e.g. /blog/my-topic). Slug is used in the URL and in the sitemap for indexing.
          </p>
          {editingBlogId && (
            <div className="admin-hint">Editing. Change fields and save, or cancel.</div>
          )}
          <div className="admin-news-grid">
            <label className="admin-label">
              Slug (URL path, e.g. my-first-post)
              <input
                className="admin-input"
                placeholder="my-topic-slug"
                value={blogSlug}
                onChange={(e) => setBlogSlug(e.target.value)}
                onBlur={() => {
                  if (!blogSlug && blogTitle) setBlogSlug(slugFromTitle(blogTitle));
                }}
              />
            </label>
            <label className="admin-label">
              Title
              <input
                className="admin-input"
                placeholder="Post title"
                value={blogTitle}
                onChange={(e) => setBlogTitle(e.target.value)}
              />
            </label>
            <label className="admin-label">
              Content (Markdown supported)
              <textarea
                className="admin-input admin-textarea"
                placeholder="Write in Markdown: **bold**, ## headings, - lists, [links](url), `code`..."
                value={blogContent}
                onChange={(e) => setBlogContent(e.target.value)}
              />
              <span className="admin-hint">Supports headings, bold, lists, links, code blocks, blockquotes, and images.</span>
            </label>
            <label className="admin-label">
              Image URL (optional)
              <input
                className="admin-input"
                placeholder="https://.../image.jpg"
                value={blogImage}
                onChange={(e) => setBlogImage(e.target.value)}
              />
            </label>
            <label className="admin-label">
              Upload image (optional)
              <input
                type="file"
                accept="image/*"
                className="admin-input"
                onChange={handleBlogImagePick}
                disabled={savingBlog}
              />
            </label>
          </div>
          {blogImage && (
            <div className="admin-news-image-preview-wrap">
              <img src={blogImage} alt="Blog preview" className="admin-news-image-preview" />
            </div>
          )}
          <div className="admin-news-actions">
            <button
              type="button"
              className="admin-btn-primary"
              onClick={handleCreateBlogPost}
              disabled={savingBlog}
            >
              {savingBlog ? "Saving…" : editingBlogId ? "Update post" : "Publish post"}
            </button>
            {editingBlogId && (
              <button
                type="button"
                className="admin-btn-secondary"
                onClick={cancelEditBlogPost}
                disabled={savingBlog}
              >
                Cancel edit
              </button>
            )}
          </div>
          {blogError && <div className="admin-error">{blogError}</div>}
          {blogInfo && <div className="admin-hint">{blogInfo}</div>}
          <div className="admin-news-list">
            {blogPosts.map((item) => (
              <div key={item.id} className="admin-news-item">
                <div className="admin-news-copy">
                  <strong>{item.title || "Untitled"}</strong>
                  <span> /blog/{item.slug || item.id}</span>
                  <span>{formatAdminDate(item.createdAt)}</span>
                </div>
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => startEditBlogPost(item)}
                  disabled={savingBlog}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => handleDeleteBlogPost(item.id)}
                  disabled={savingBlog}
                >
                  Delete
                </button>
              </div>
            ))}
            {blogPosts.length === 0 && <div className="admin-hint">No blog posts yet.</div>}
          </div>
        </section>

        <div className="admin-actions">
          <button
            type="button"
            className="admin-btn-primary"
            onClick={handleSave}
          >
            Save configuration
          </button>
          <button
            type="button"
            className="admin-btn-secondary"
            onClick={clearSelection}
          >
            Clear selection
          </button>
        </div>
          </>
        )}

        {activeTab === "users" && (
          <section className="admin-users-section">
            <h2 className="admin-section-title">User stats</h2>
            <p className="admin-subtitle">
              Registered users with aggregated gameplay statistics.
            </p>
            {usersError && <div className="admin-error">{usersError}</div>}
            <div className="admin-table admin-users-table">
              <div className="admin-table-header">
                <span>User</span>
                <span>UID</span>
                <span>Registered</span>
                <span>Games played</span>
                <span>Total playtime</span>
              </div>
              {usersLoading && <div className="admin-hint">Loading users...</div>}
              {!usersLoading &&
                users.map((u) => {
                  const gamesPlayed = Number(u.totalPlays) || 0;
                  const playtimeSeconds = Number(u.totalPlaytimeSeconds) || 0;
                  const displayName = (u.username || u.displayName || "").trim() || "Anonymous";
                  const registeredAt = formatAdminDate(u.createdAt);
                  return (
                    <div
                      key={u.id}
                      className="admin-row admin-user-row"
                      onClick={() => setSelectedUser(u)}
                    >
                      <div className="admin-cell admin-cell-title">{displayName}</div>
                      <div className="admin-cell">{u.id}</div>
                      <div className="admin-cell">{registeredAt}</div>
                      <div className="admin-cell">{gamesPlayed}</div>
                      <div className="admin-cell">
                        {formatDurationShort(playtimeSeconds)}
                      </div>
                    </div>
                  );
                })}
              {!usersLoading && users.length === 0 && !usersError && (
                <div className="admin-hint">No users found.</div>
              )}
            </div>
          </section>
        )}

        {selectedUser && selectedUserStats && (
          <div
            className="profile-badges-modal-backdrop"
            onClick={() => setSelectedUser(null)}
          >
            <div
              className="profile-badges-modal admin-user-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="profile-badges-modal-head">
                <div>
                  <h2 className="admin-section-title">{selectedUserStats.username}</h2>
                  <p className="profile-badges-modal-sub">
                    UID: {selectedUser.id} · Registered: {selectedUserStats.registeredAt}
                  </p>
                </div>
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => setSelectedUser(null)}
                >
                  Close
                </button>
              </div>

              <div className="profile-stats-grid">
                <div className="profile-stat-card">
                  <p className="profile-stat-label">Games played</p>
                  <p className="profile-stat-value">{selectedUserStats.gamesPlayed}</p>
                </div>
                <div className="profile-stat-card">
                  <p className="profile-stat-label">Total playtime</p>
                  <p className="profile-stat-value">
                    {formatDurationShort(selectedUserStats.totalPlaytimeSeconds)}
                  </p>
                </div>
                <div className="profile-stat-card">
                  <p className="profile-stat-label">Avg. session</p>
                  <p className="profile-stat-value-sm">
                    {formatDurationShort(selectedUserStats.averageSessionSeconds)}
                  </p>
                </div>
                <div className="profile-stat-card">
                  <p className="profile-stat-label">Streak</p>
                  <p className="profile-stat-value">{selectedUserStats.streakCount}</p>
                </div>
              </div>

              {selectedUserStats.topPlayedGame && (
                <div className="profile-note-card">
                  <p className="profile-note-label">Top game</p>
                  <p className="profile-note-text">
                    {selectedUserStats.topPlayedGame.title} ·{" "}
                    {formatDurationShort(selectedUserStats.topPlayedGame.seconds)}
                  </p>
                </div>
              )}

              <div className="profile-pref-grid">
                <div className="profile-pref-group">
                  <h3 className="profile-pref-title">Favorite genres</h3>
                  {selectedUserStats.genreEntries.length ? (
                    <ul className="profile-pref-list">
                      {selectedUserStats.genreEntries.map(([name, count]) => (
                        <li key={name}>
                          {name} · <span>{count}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="profile-note-text">No genre data yet.</p>
                  )}
                </div>
                <div className="profile-pref-group">
                  <h3 className="profile-pref-title">Favorite categories</h3>
                  {selectedUserStats.categoryEntries.length ? (
                    <ul className="profile-pref-list">
                      {selectedUserStats.categoryEntries.map(([name, count]) => (
                        <li key={name}>
                          {name} · <span>{count}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="profile-note-text">No category data yet.</p>
                  )}
                </div>
              </div>

              <div className="profile-recent-block">
                <h3 className="profile-pref-title">Recent games</h3>
                {selectedUserStats.recentSessions.length ? (
                  <ul className="profile-pref-list">
                    {selectedUserStats.recentSessions.map((session) => (
                      <li key={session.id + (session.slug || "")}>{session.title}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="profile-note-text">No recent games recorded.</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default Admin;
